import { env } from "@/env/client";
import { CONTACT } from "@/lib/site";
import { cn } from "@/ui/utils/cn";
import { normalizeEgyptianMobile } from "@/utils/phone";
import { track } from "@services/analytics";
import { TranslateFn, useTranslations } from "@services/strings";
import { useForm, ValidationError } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { zodValidator } from "@tanstack/zod-form-adapter";
import axios from "axios";
import { Check } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useId } from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { z } from "zod";

/** What the visitor wants to talk about. */
const INTERESTS = [
  "unlock",
  "payLess",
  "mortgage",
  "auto",
  "flex",
  "developer",
] as const;

export type Interest = (typeof INTERESTS)[number];

/**
 * The product codes `/api/website-enquiry` accepts — the same list as
 * `packages/data-model/services/website-enquiries/types.ts`, kept inline so
 * the site does not pull the data model into its build.
 */
export const toApiProduct = (
  interest: Interest,
): "UNLOCK" | "PAY_LESS" | "MORTGAGE" | "AUTO_LOAN" | "FLEX" | "DEVELOPER" =>
  (
    ({
      unlock: "UNLOCK",
      payLess: "PAY_LESS",
      mortgage: "MORTGAGE",
      auto: "AUTO_LOAN",
      flex: "FLEX",
      developer: "DEVELOPER",
    }) as const
  )[interest];

/** What the visitor typed. */
export type EnquiryValues = {
  firstName: string;
  lastName: string;
  phone: string;
  interest: Interest;
  consent: boolean;
};

/**
 * What the visitor can get wrong, with the messages in their language. The
 * API re-validates the payload against `websiteEnquirySchema`; this is the
 * part worth catching before the round trip.
 */
export const enquirySchema = (t: TranslateFn) =>
  z.object({
    firstName: z.string().trim().min(1, t("site.form.required")),
    lastName: z.string().trim().min(1, t("site.form.required")),
    phone: z
      .string()
      .refine(
        (value) => normalizeEgyptianMobile(value) !== null,
        t("site.form.invalidPhone"),
      ),
    interest: z.enum(INTERESTS),
    consent: z.literal(true, { message: t("site.form.required") }),
  });

/** Mints the reCAPTCHA token, posts the enquiry, and records the submission once the API has accepted it. */
const useSendEnquiry = (placement: string) => {
  const { locale } = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();

  return useMutation({
    mutationFn: async (values: EnquiryValues) => {
      if (!executeRecaptcha) {
        throw new Error("reCAPTCHA not ready");
      }
      const gRecaptchaToken = await executeRecaptcha("websiteEnquiry");
      await axios.post("/api/website-enquiry", {
        gRecaptchaToken,
        data: {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          phone: normalizeEgyptianMobile(values.phone),
          product: toApiProduct(values.interest),
          source: placement,
          locale,
        },
      });
    },
    onSuccess: (_, values) => {
      track({
        event: "Button Clicked",
        properties: {
          label: `Enquiry submitted - ${values.interest} - ${placement}`,
        },
      });
    },
  });
};

const inputClass =
  "h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-base text-ink placeholder:text-slate-400 focus:border-navy focus:outline-none focus:ring-2 focus:ring-mint";

const FieldErrors = ({ errors }: { errors: ValidationError[] }) => {
  if (errors.length === 0) {
    return null;
  }
  return (
    <span className="text-sm text-red-600" role="alert">
      {errors.join(", ")}
    </span>
  );
};

const Field = ({
  id,
  label,
  errors = [],
  children,
}: {
  id: string;
  label: string;
  errors?: ValidationError[];
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-sm font-medium text-ink">
      {label}
    </label>
    {children}
    <FieldErrors errors={errors} />
  </div>
);

const InterestSelect = ({
  id,
  value,
  onChange,
}: {
  id: string;
  value: Interest;
  onChange: (interest: Interest) => void;
}) => {
  const { t } = useTranslations();
  return (
    <Field id={id} label={t("site.form.interest")}>
      <select
        id={id}
        className={inputClass}
        value={value}
        onChange={(e) => onChange(e.target.value as Interest)}
      >
        {INTERESTS.map((option) => (
          <option key={option} value={option}>
            {t(`site.form.interests.${option}`)}
          </option>
        ))}
      </select>
    </Field>
  );
};

const ConsentField = ({
  checked,
  onChange,
  onBlur,
  errors,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  onBlur: () => void;
  errors: ValidationError[];
}) => {
  const { t } = useTranslations();
  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex items-start gap-3 text-sm text-slate-600">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0 accent-navy"
          checked={checked}
          onBlur={onBlur}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>
          {t("site.form.consent")}{" "}
          <Link
            href="/static/privacy-policy"
            className="underline hover:text-ink"
          >
            {t("site.form.privacy")}
          </Link>
        </span>
      </label>
      <FieldErrors errors={errors} />
    </div>
  );
};

const SubmitError = () => {
  const { t } = useTranslations();
  return (
    <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700" role="alert">
      <p className="font-semibold">{t("site.form.errorTitle")}</p>
      <p>{t("site.form.errorBody", { email: CONTACT.email })}</p>
    </div>
  );
};

const SubmitButton = ({ submitting }: { submitting: boolean }) => {
  const { t } = useTranslations();
  return (
    <button
      type="submit"
      disabled={submitting}
      className="inline-flex h-12 items-center justify-center rounded-full bg-ink px-8 text-base font-semibold text-white transition-colors hover:bg-navy disabled:opacity-60"
    >
      {submitting ? t("site.form.submitting") : t("site.form.submit")}
    </button>
  );
};

const SuccessPanel = ({ className }: { className?: string }) => {
  const { t } = useTranslations();
  return (
    <div
      className={cn("rounded-3xl bg-mint-soft p-8 text-ink", className)}
      role="status"
    >
      <span className="grid h-10 w-10 place-items-center rounded-full bg-mint text-ink">
        <Check size={20} />
      </span>
      <p className="mt-4 text-h3 font-semibold">
        {t("site.form.successTitle")}
      </p>
      <p className="mt-2 text-body text-slate-700">
        {t("site.form.successBody")}
      </p>
    </div>
  );
};

type EnquiryFormProps = {
  defaultInterest: Interest;
  /** Hide the interest selector when the page is already about one product. */
  lockInterest?: boolean;
  /** Where on the site the form sits — goes into analytics. */
  placement: string;
  className?: string;
};

const EnquiryFormInner = ({
  defaultInterest,
  lockInterest = false,
  placement,
  className,
}: EnquiryFormProps) => {
  const { t } = useTranslations();
  const uid = useId();
  const schema = enquirySchema(t);
  const send = useSendEnquiry(placement);
  const form = useForm<EnquiryValues, typeof zodValidator>({
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      interest: defaultInterest,
      consent: false,
    },
    validatorAdapter: zodValidator,
    onSubmit: ({ value }) => send.mutate(value),
  });

  if (send.isSuccess) {
    return <SuccessPanel className={className} />;
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      noValidate
      className={cn("flex flex-col gap-5", className)}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <form.Field
          name="firstName"
          validators={{ onChange: schema.shape.firstName }}
        >
          {(field) => (
            <Field
              id={`${uid}-first`}
              label={t("site.form.firstName")}
              errors={field.state.meta.errors}
            >
              <input
                id={`${uid}-first`}
                className={inputClass}
                autoComplete="given-name"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            </Field>
          )}
        </form.Field>
        <form.Field
          name="lastName"
          validators={{ onChange: schema.shape.lastName }}
        >
          {(field) => (
            <Field
              id={`${uid}-last`}
              label={t("site.form.lastName")}
              errors={field.state.meta.errors}
            >
              <input
                id={`${uid}-last`}
                className={inputClass}
                autoComplete="family-name"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
            </Field>
          )}
        </form.Field>
      </div>

      <form.Field name="phone" validators={{ onChange: schema.shape.phone }}>
        {(field) => (
          <Field
            id={`${uid}-phone`}
            label={t("site.form.phone")}
            errors={field.state.meta.errors}
          >
            <input
              id={`${uid}-phone`}
              className={cn(inputClass, "tabular")}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              dir="ltr"
              placeholder="01x xxxx xxxx"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
          </Field>
        )}
      </form.Field>

      {lockInterest ? null : (
        <form.Field name="interest">
          {(field) => (
            <InterestSelect
              id={`${uid}-interest`}
              value={field.state.value}
              onChange={field.handleChange}
            />
          )}
        </form.Field>
      )}

      <form.Field
        name="consent"
        validators={{ onChange: schema.shape.consent }}
      >
        {(field) => (
          <ConsentField
            checked={field.state.value}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            errors={field.state.meta.errors}
          />
        )}
      </form.Field>

      {send.isError ? <SubmitError /> : null}

      <SubmitButton submitting={send.isLoading} />
    </form>
  );
};

/** Callback request form posting to `/api/website-enquiry`, with reCAPTCHA v3. */
export const EnquiryForm = (props: EnquiryFormProps) => (
  <GoogleReCaptchaProvider reCaptchaKey={env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}>
    <EnquiryFormInner {...props} />
  </GoogleReCaptchaProvider>
);
